import * as React from "react";
import { IconButton, Menu, MenuItem, Button } from "@material-ui/core";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import styled from "styled-components";
import Theme from "../Theme";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { IApplicationState } from "../store";
import { selectAuth } from "../store/auth/selectors";
import Roles from "../models/Roles";
import ICompany from "../models/ICompany";
import { selectCompany } from "../store/context/selectors";
import { ApplicationPaths, QueryParameterNames } from "./api-authorization/ApiAuthorizationConstants";
import getUrl from "../functions/getUrl";

const AccountMenuButton = styled(IconButton)`
  border-radius: 0;
  padding-right: 40px;
  height: 66px;
`;

const AccountMenu = styled(Menu)`
  & .MuiMenu-paper {
    background-color: ${Theme.colors.darkBackground};
    border: 1px solid ${Theme.colors.accentCool};
  }

  & .MuiPaper-root {
    color: ${Theme.colors.white};
  }
`;

const UserDetails = styled.div`
  color: ${Theme.colors.white};
  margin-left: 10px;
`;

const Username = styled.div`
  font-size: ${Theme.sizes.menuDefault};
  text-transform: uppercase;
`;

const UserCompany = styled.div`
  font-size: 14px;
`;

const AccountLink = styled(Link)`
  text-decoration: none;
`;

const AccountButton = styled(Button)`
  color: ${Theme.colors.white};
  font-size: ${Theme.sizes.menuDefault};
  text-transform: uppercase;
`;

const MenuItemLink = styled(Link)`
  color: ${Theme.colors.white};
  text-decoration: none;
`;

export interface IAccountMenuIconProps extends RouteComponentProps {
  isAuthenticated: boolean;
  username: string;
  company?: ICompany;
  userRoles: Array<string>;
  contactId: number;
}

const AccountMenuIcon = (props: IAccountMenuIconProps) => {
  const {
    history,
    location: { pathname },
    isAuthenticated,
    username,
    company,
    userRoles,
    contactId,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLogout = (event: React.MouseEvent<HTMLElement>) => {
    handleClose();
    history.push(ApplicationPaths.LogOut, { local: getUrl("/") });
  };

  const handleLogin = (event: React.MouseEvent<HTMLElement>) => {
    handleClose();
    const redirectUrl = `${ApplicationPaths.Login}?${QueryParameterNames.ReturnUrl}=${encodeURI(window.location.href)}`;
    history.push(redirectUrl);
  };

  const getAdminPanelMenu = () => {
    if (userRoles && userRoles.includes(Roles.Admin)) {
      return (
        <MenuItem>
          <MenuItemLink to="/admin-panel" onClick={handleClose}>
            Admin Panel
          </MenuItemLink>
        </MenuItem>
      );
    }
  };

  const getContactMaintenanceMenu = () => {
    if (userRoles && (userRoles.includes(Roles.Admin) || userRoles.includes(Roles.ContactManager))) {
      return (
        <MenuItem>
          <MenuItemLink to="/users/list" onClick={handleClose}>
            Manage Users
          </MenuItemLink>
        </MenuItem>
      );
    }
  };

  const getNotificationMaintenanceMenu = () => {
    if (contactId > 0) {
      return (
        <MenuItem>
          <MenuItemLink to="/notifications/manage" onClick={handleClose}>
            Manage Notifications
          </MenuItemLink>
        </MenuItem>
      );
    }
  };

  const getIntegrationMaintenanceMenu = () => {
    if (userRoles && userRoles.includes(Roles.User)) {
      return (
        <MenuItem>
          <MenuItemLink to="/partnership/integrations/my" onClick={handleClose}>
            Manage Integrations
          </MenuItemLink>
        </MenuItem>
      );
    }
  };

  if (isAuthenticated) {
    return (
      <div>
        <AccountMenuButton aria-label="account of current user" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleMenu} color="inherit">
          <AccountCircleIcon />
          <UserDetails>
            <Username>{username}</Username>
            {company && <UserCompany>{company.Name}</UserCompany>}
          </UserDetails>
        </AccountMenuButton>
        <AccountMenu
          id="menu-appbar"
          elevation={4}
          anchorEl={anchorEl}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={open}
          onClose={handleClose}
        >
          {getAdminPanelMenu()}
          {getContactMaintenanceMenu()}
          {getNotificationMaintenanceMenu()}
          {getIntegrationMaintenanceMenu()}
          <MenuItem onClick={handleLogout}>Sign out</MenuItem>
        </AccountMenu>
      </div>
    );
  } else {
    return (
      <div>
        <AccountLink to="/apply">
          <AccountButton>Apply</AccountButton>
        </AccountLink>
        |
        <AccountButton color="inherit" onClick={handleLogin}>
          Log in
        </AccountButton>
      </div>
    );
  }
};

const connected = connect((state: IApplicationState) => ({
  ...selectAuth(state),
  company: selectCompany(state),
}))(AccountMenuIcon);

export default withRouter(connected);
